(() => {
  if (customElements.get("aa-product-grid")) return;

  class AAProductGrid extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.sequence = (this.sequence || 0) + 1;
      this.committedURL = location.href;
      this.committedHTML = this.innerHTML;
      const options = { signal: this.events.signal };
      this.addEventListener("click", (event) => this.onClick(event), options);
      this.addEventListener("change", (event) => this.onChange(event), options);
      this.addEventListener("input", (event) => this.onInput(event), options);
      this.addEventListener(
        "focusin",
        (event) => this.onSwatchFocus(event),
        options,
      );
      this.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Escape") this.closeSort(true);
        },
        options,
      );
      document.addEventListener(
        "click",
        (event) => {
          if (
            !this.querySelector("[data-aa-sort-dropdown]")?.contains(
              event.target,
            )
          )
            this.closeSort();
        },
        options,
      );
      window.addEventListener(
        "popstate",
        () => this.load(new URL(location.href), "none"),
        options,
      );
      this.resize = new ResizeObserver(() => this.placePromotion());
      this.resize.observe(this);
      this.enhance();
    }

    disconnectedCallback() {
      this.sequence += 1;
      this.request?.abort();
      this.events?.abort();
      this.resize?.disconnect();
      this.events = null;
    }

    parameters(incoming) {
      const url = new URL(location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (
          key.startsWith("filter.") ||
          ["sort_by", "page", "section_id"].includes(key) ||
          incoming.has(key)
        ) {
          url.searchParams.delete(key);
        }
      }
      for (const [key, value] of incoming) {
        if (value !== "" && key !== "section_id")
          url.searchParams.append(key, value);
      }
      if (this.dataset.view && !url.searchParams.has("view"))
        url.searchParams.set("view", this.dataset.view);
      return url;
    }

    focusKey() {
      const element = document.activeElement;
      if (!this.contains(element)) return null;
      if (element.matches("[data-aa-sort-radio], [data-aa-sort-trigger]"))
        return { sort: true };
      if (element.name) return { name: element.name, value: element.value };
      return { summary: true };
    }

    restoreFocus(key) {
      if (!key) return;
      let element;
      if (key.name)
        element = [...this.querySelectorAll("input")].find(
          (input) =>
            input.name === key.name &&
            input.value === key.value &&
            !input.disabled,
        );
      if (key.sort) element = this.querySelector("[data-aa-sort-trigger]");
      if (!element || element.type === "hidden")
        element = this.querySelector(
          ".product-grid__summary-total, [data-aa-results-status]",
        );
      if (element) {
        if (!element.matches("input, button, a")) element.tabIndex = -1;
        element.focus({ preventScroll: true });
      }
    }

    async load(target, historyMode = "push") {
      if (
        target.origin !== location.origin ||
        target.pathname !== location.pathname
      )
        return;
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const sequence = ++this.sequence;
      const focus = this.focusKey();
      const sidebarHidden = this.querySelector(
        "[data-aa-grid-layout]",
      )?.classList.contains("is-filters-hidden");
      this.setBusy(true);
      this.querySelector("[data-aa-grid-error]").hidden = true;
      const sectionURL = new URL(target);
      sectionURL.searchParams.set("section_id", this.dataset.sectionId);
      try {
        const response = await fetch(sectionURL, { signal: request.signal });
        if (!response.ok)
          throw new Error(`Section response ${response.status}`);
        const html = await response.text();
        if (sequence !== this.sequence || !this.isConnected) return;
        const document = new DOMParser().parseFromString(html, "text/html");
        const section = document.getElementById(this.id);
        if (
          section?.tagName !== "AA-PRODUCT-GRID" ||
          section.dataset.sectionId !== this.dataset.sectionId ||
          section.dataset.collectionUrl !== this.dataset.collectionUrl ||
          !section.querySelector("[data-aa-products-container]") ||
          !section.querySelector("[data-aa-grid-layout]") ||
          !section.querySelector("[data-aa-grid-error]") ||
          !section.querySelector("[data-aa-results-status]")
        ) {
          throw new Error("Unexpected collection section");
        }
        this.innerHTML = section.innerHTML;
        if (historyMode === "push" && target.href !== location.href)
          history.pushState({ ...history.state, aaGrid: true }, "", target);
        if (historyMode === "replace")
          history.replaceState({ ...history.state, aaGrid: true }, "", target);
        this.committedURL = target.href;
        this.committedHTML = this.innerHTML;
        this.enhance();
        if (sidebarHidden) this.toggleSidebar(true);
        this.restoreFocus(focus);
      } catch (error) {
        if (
          sequence !== this.sequence ||
          error.name === "AbortError" ||
          !this.isConnected
        )
          return;
        this.innerHTML = this.committedHTML;
        if (location.href !== this.committedURL)
          history.replaceState(history.state, "", this.committedURL);
        this.enhance();
        if (sidebarHidden) this.toggleSidebar(true);
        this.querySelector("[data-aa-grid-error]").hidden = false;
        this.retryURL = target;
        this.retryMode = historyMode === "none" ? "replace" : historyMode;
        this.restoreFocus(focus);
      } finally {
        if (sequence === this.sequence && this.isConnected) this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.setAttribute("aria-busy", String(busy));
      this.querySelector("[data-aa-loading-overlay]")?.classList.toggle(
        "is-loading",
        busy,
      );
      const status = this.querySelector("[data-aa-results-status]");
      if (status)
        status.textContent = busy
          ? this.dataset.loadingLabel
          : this.querySelector(
              ".product-grid__summary-total",
            )?.textContent.trim() || "";
    }

    submit() {
      const form = this.querySelector("[data-aa-filters-form]");
      if (!form?.checkValidity()) {
        form?.reportValidity();
        return;
      }
      const params = new URLSearchParams(new FormData(form));
      params.delete("page");
      this.load(this.parameters(params));
    }

    onChange(event) {
      const input = event.target;
      if (input.matches("[data-aa-sort-radio]")) {
        if (!input.checked) return;
        const form = this.querySelector("[data-aa-filters-form]");
        const params = form
          ? new URLSearchParams(new FormData(form))
          : new URLSearchParams(location.search);
        params.set("sort_by", input.value);
        params.delete("page");
        this.closeSort();
        this.load(this.parameters(params));
      } else if (input.closest("[data-aa-filters-form]")) {
        input
          .closest(".product-grid__size-btn")
          ?.classList.toggle("is-active", input.checked);
        this.submit();
      }
    }

    onInput(event) {
      const input = event.target;
      const min = this.querySelector("[data-dual-slider-min]");
      const max = this.querySelector("[data-dual-slider-max]");
      const minInput = this.querySelector("[data-price-min-input]");
      const maxInput = this.querySelector("[data-price-max-input]");
      if (!min || !max || !minInput || !maxInput) return;
      if (input === min) {
        min.value = Math.min(Number(min.value), Number(max.value));
        minInput.value = min.value;
      } else if (input === max) {
        max.value = Math.max(Number(max.value), Number(min.value));
        maxInput.value = max.value;
      } else if (input === minInput) min.value = minInput.value || 0;
      else if (input === maxInput) max.value = maxInput.value || max.max;
      else return;
      this.sliderProgress();
    }

    sliderProgress() {
      const min = this.querySelector("[data-dual-slider-min]");
      const max = this.querySelector("[data-dual-slider-max]");
      const progress = this.querySelector("[data-slider-progress]");
      if (!min || !max || !progress) return;
      const limit = Number(max.max) || 1;
      const left = (Number(min.value) / limit) * 100;
      progress.style.left = `${left}%`;
      progress.style.width = `${Math.max(0, (Number(max.value) / limit) * 100 - left)}%`;
    }

    onClick(event) {
      const target = event.target.closest("button, a");
      if (!target || !this.contains(target)) return;
      if (target.matches("[data-swatches-toggle]")) {
        const group = target.closest(".product-block__swatches--sibling");
        const expanded = target.getAttribute("aria-expanded") !== "true";
        this.disclose(group, target, expanded);
      } else if (target.matches("[data-aa-filter-toggle]"))
        this.toggleSidebar();
      else if (target.matches("[data-filter-accordion]")) {
        const group = target.closest("[data-filter-group]");
        const open = group.classList.toggle("is-open");
        target.setAttribute("aria-expanded", String(open));
      } else if (target.matches("[data-aa-sort-trigger]")) {
        const open = target
          .closest("[data-aa-sort-dropdown]")
          .classList.toggle("is-open");
        target.setAttribute("aria-expanded", String(open));
      } else if (target.matches("[data-aa-retry]")) {
        if (this.retryURL) this.load(this.retryURL, this.retryMode);
      } else if (
        target.matches(
          "[data-aa-clear-all], [data-aa-filter-remove], [data-aa-pagination] a",
        )
      ) {
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button > 0
        )
          return;
        event.preventDefault();
        const incoming = target.matches("[data-aa-clear-all]")
          ? new URLSearchParams({
              sort_by:
                this.querySelector("[data-aa-sort-radio]:checked")?.value ||
                "best-selling",
            })
          : new URL(target.href).searchParams;
        this.load(this.parameters(incoming));
      } else return;
      if (target.tagName === "BUTTON") event.preventDefault();
    }

    toggleSidebar(force) {
      const layout = this.querySelector("[data-aa-grid-layout]");
      if (!layout) return;
      const hidden =
        force === undefined
          ? !layout.classList.contains("is-filters-hidden")
          : force;
      layout.classList.toggle("is-filters-hidden", hidden);
      const button = this.querySelector("[data-aa-filter-toggle]");
      if (button) {
        button.setAttribute("aria-expanded", String(!hidden));
        button.querySelector(".product-grid__filter-toggle-text").textContent =
          hidden ? button.dataset.showLabel : button.dataset.hideLabel;
      }
      this.placePromotion();
    }

    closeSort(focus = false) {
      const dropdown = this.querySelector("[data-aa-sort-dropdown]");
      const trigger = this.querySelector("[data-aa-sort-trigger]");
      if (!dropdown?.classList.contains("is-open")) return;
      dropdown.classList.remove("is-open");
      trigger?.setAttribute("aria-expanded", "false");
      if (focus) trigger?.focus();
    }

    enhance() {
      this.querySelectorAll(".product-block__swatches--sibling").forEach(
        (group, index) => {
          const links = [
            ...group.querySelectorAll("a.product-block__swatches-link"),
          ];
          const title =
            group
              .closest(".product-block")
              .querySelector(".product-block__title")
              ?.textContent.trim() || "";
          links.forEach((link) => {
            link.setAttribute(
              "aria-label",
              `${title} — ${link.querySelector("[data-option-item]")?.dataset.optionItem || ""}`,
            );
          });
          if (
            links.length <= 6 ||
            group.querySelector("[data-swatches-toggle]")
          )
            return;
          group.id = `${this.id}-swatches-${index}`;
          const button = document.createElement("button");
          button.type = "button";
          button.dataset.swatchesToggle = "";
          button.setAttribute("aria-controls", group.id);
          group.append(button);
          this.disclose(group, button, false);
        },
      );
      this.sliderProgress();
      this.placePromotion();
      this.setBusy(false);
    }

    disclose(group, button, expanded) {
      const links = [
        ...group.querySelectorAll("a.product-block__swatches-link"),
      ];
      links.forEach((link, index) => {
        link.hidden = !expanded && index >= 6;
      });
      button.setAttribute("aria-expanded", String(expanded));
      button.textContent = expanded
        ? this.dataset.lessLabel
        : this.dataset.moreLabel.replace("__count__", String(links.length - 6));
    }

    onSwatchFocus(event) {
      const link = event.target.closest(
        ".product-block__swatches--sibling a.product-block__swatches-link",
      );
      if (!link) return;
      const card = link.closest(".product-block");
      const id = link.querySelector("[data-id]")?.dataset.id;
      const image = [
        ...card.querySelectorAll(".product-block__image[data-id]"),
      ].find((item) => item.dataset.id === id);
      if (!image) return;
      card.querySelectorAll(".product-block__image").forEach((item) => {
        item.style.display = item === image ? "block" : "none";
      });
      const title = card.querySelector(".product-block__title-link");
      if (title) title.setAttribute("href", image.getAttribute("href"));
    }

    placePromotion() {
      const grid = this.querySelector("[data-aa-products-container]");
      const promo = grid?.querySelector(".product-grid__promo");
      if (!promo) return;
      promo.style.gridColumn = "1";
      promo.style.gridRow = "auto";
      const columns = Math.max(
        1,
        getComputedStyle(grid).gridTemplateColumns.split(/\s+/).length,
      );
      const column =
        parseInt(promo.style.getPropertyValue("--promo-col"), 10) || 1;
      const row =
        parseInt(promo.style.getPropertyValue("--promo-row"), 10) || 1;
      promo.style.gridColumn = String(Math.max(1, Math.min(column, columns)));
      promo.style.gridRow = String(
        Math.max(
          1,
          Math.min(
            row,
            Math.ceil(
              (grid.querySelectorAll(".product-block").length + 1) / columns,
            ),
          ),
        ),
      );
    }
  }

  customElements.define("aa-product-grid", AAProductGrid);
})();
